/**
 * Page Profil Unifiee
 * Fusionne Profil Accord Cadre et Profil Prestataire Libre
 * L'affichage s'adapte selon le role de l'utilisateur connecte
 * 
 * FRONT-08: Meme interface pour PL et AC, avec des onglets adaptes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ProfileProvider, useProfile } from '../../contexts/ProfileContext';
import TabNav from '../../composants/TabNav/TabNav';
import Button from '../../composants/Button/Button';
import { useProfilType } from './hooks/useProfilType';

// Onglets communs PL + AC
import TabPresentation from './tabs/TabPresentation';
import TabDocuments from './tabs/TabDocuments';
import TabReferences from './tabs/TabReferences';
import TabCoordonnees from './tabs/TabCoordonnees';
import TabBancaire from './tabs/TabBancaire';
import TabEquipe from './tabs/TabEquipe';

// Composants specifiques
import ProgressionBar from './components/ProgressionBar';
import AlertMessage from './components/AlertMessage';

import './ProfilUnifie.css';

/**
 * Contenu du profil unifie (utilise ProfileContext)
 * FRONT-08: Fonctionne pour PL ET AC
 */
const ProfilUnifieContent: React.FC = () => {
  const {
    profile,
    completion,
    isLoading,
    isSaving,
    error,
    successMessage,
    loadProfile,
    clearMessages,
  } = useProfile();

  const profilType = useProfilType();
  const [activeTab, setActiveTab] = useState<number>(0);

  // FRONT-08: Onglets adaptes selon le type de profil
  // Prestataire Libre : tous les onglets avec References
  // Accord Cadre : onglets essentiels (pas de References)
  const TABS_PL = [
    'Présentation',
    'Documents',
    'Références',
    'Bancaire',
    'Gestion d\'équipe',
  ];

  const TABS_AC = [
    'Présentation',
    'Documents',
    'Références',
    'Bancaire',
    'Gestion d\'équipe',
  ];

  // Selection des onglets selon le type
  const tabs = profilType.isPrestataireLibre ? TABS_PL : TABS_AC;

  // Charger le profil au montage
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Determiner quels onglets ont des problemes (non completes)
  // CORE-15: AC voit aussi les indicateurs de completion comme PL
  // FRONT-12: References a l'index 2, Bancaire (avec Coordonnees) a l'index 3
  const getTabsWithIssues = useCallback((): number[] => {
    if (!completion?.sections) return [];

    const issues: number[] = [];

    Object.entries(completion.sections).forEach(([name, complete]) => {
      if (!complete) {
        switch (name) {
          case 'presentation':
            issues.push(0);
            break;
          case 'documents':
            issues.push(1);
            break;
          case 'references':
            issues.push(2);
            break;
          case 'coordonnees':
            // FRONT-11: Coordonnees fusionne dans Bancaire (index 3)
            issues.push(3);
            break;
          case 'bancaire':
            issues.push(3);
            break;
        }
      }
    });

    // Retirer les doublons
    return Array.from(new Set(issues));
  }, [completion]);

  // FRONT-08 + FRONT-21: Rendu du contenu selon l'onglet actif
  // FRONT-21: TabDocuments détecte automatiquement le rôle (AC ou PL) sans prop
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <TabPresentation />;
      case 1:
        return <TabDocuments />;
      case 2:
        return <TabReferences />;
      case 3:
        return <TabBancaire />;
      case 4:
        return <TabEquipe />;
      default:
        return <TabPresentation />;
    }
  };

  // Etat de chargement initial
  if (isLoading && !profile) {
    return (
      <div className="profil-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement du profil...</p>
        </div>
      </div>
    );
  }

  // Erreur de chargement
  if (!isLoading && !profile && error) {
    return (
      <div className="profil-container">
        <div className="error-container">
          <h2>Erreur de chargement</h2>
          <p>{error}</p>
          <Button value="Reessayer" onClick={loadProfile} />
        </div>
      </div>
    );
  }

  return (
    <div className="profil-container">
      {/* En-tete avec progression */}
      <header className="profil-header">
        <div className="header-left">
          <h1>Profil</h1>
          {/* {profile?.entreprise?.nom && (
            <p className="entreprise-nom">{profile.entreprise.nom}</p>
          )} */}
        </div>
        {profilType.showProgression && (
          <div className="header-right">
            <ProgressionBar
              percentage={completion?.percent || 0}
              isComplete={completion?.is_complete || false}
              visible={profile?.entite?.visible || false}
            />
          </div>
        )}
      </header>

      {/* Messages d'alerte */}
      {(error || successMessage) && (
        <AlertMessage
          type={error ? 'error' : 'success'}
          message={error || successMessage || ''}
          onClose={clearMessages}
        />
      )}

      {/* Navigation par onglets */}
      <div className="profil-tabs">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          notifications={getTabsWithIssues().map(idx => ({ tabIndex: idx, count: 1 }))}
        />
      </div>

      {/* Contenu de l'onglet */}
      <div className="profil-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

/**
 * Composant principal unifie
 * FRONT-08: Meme wrapper pour PL ET AC
 * Utilise ProfileProvider pour les deux types
 */
const ProfilUnifie: React.FC = () => {
  return (
    <ProfileProvider>
      <ProfilUnifieContent />
    </ProfileProvider>
  );
};

export default ProfilUnifie;
